import { API_URL } from './constant';

let cachedPrefix = null;
let cachedTinymceApiKey = null;
let fetchPromise = null;

export const fetchRoutePrefix = async () => {
  // Return cached value if already fetched
  if (cachedPrefix !== null) {
    return cachedPrefix;
  }

  // Return existing promise if fetch is in progress
  if (fetchPromise) {
    return fetchPromise;
  }

  // Start new fetch
  fetchPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
      }

      const result = await response.json();
      const prefix = result?.data?.prefix || null;
      
      // Normalize prefix: remove leading/trailing slashes
      const normalizedPrefix = prefix 
        ? prefix.replace(/^\/+|\/+$/g, '').trim() 
        : null;

      cachedPrefix = normalizedPrefix || null;
      
      // Cache tinymceApiKey from backend config
      cachedTinymceApiKey = result?.data?.tinymceApikey || result?.data?.tinymceApiKey || null;
      
      return cachedPrefix;
    } catch (error) {
      console.error('Error fetching route prefix:', error);
      // Default to no prefix on error
      cachedPrefix = null;
      return null;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

export const getCachedPrefix = () => {
  return cachedPrefix;
};

export const clearPrefixCache = () => {
  cachedPrefix = null;
  cachedTinymceApiKey = null;
  fetchPromise = null;
};

export const getCachedTinymceApiKey = () => {
  return cachedTinymceApiKey || process.env.REACT_APP_TINYMCE_API_KEY || null;
};

export const getPathWithPrefix = (path) => {
  const prefix = getCachedPrefix();
  if (!prefix) return path;
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `/${prefix}${normalizedPath}`;
};

export const getCurrentPathWithoutPrefix = () => {
  const prefix = getCachedPrefix();
  const currentPath = window.location.pathname;
  
  if (!prefix) return currentPath;
  
  const prefixPattern = new RegExp(`^/${prefix}(/|$)`);
  if (prefixPattern.test(currentPath)) {
    return currentPath.replace(prefixPattern, '/');
  }
  
  return currentPath;
};

export const isUrlPrefixValid = () => {
  const prefix = getCachedPrefix();
  const currentPath = window.location.pathname;
  
  if (!prefix) {
    // No prefix configured - URL should not start with any prefix
    // Allow root and direct paths
    return true;
  }
  
  // Prefix configured - URL should start with it
  const prefixPattern = new RegExp(`^/${prefix}(/|$)`);
  return prefixPattern.test(currentPath);
};

export const getCorrectedUrl = () => {
  const prefix = getCachedPrefix();
  const currentPath = window.location.pathname;
  const search = window.location.search;
  const hash = window.location.hash;
  
  let correctedPath = currentPath;
  
  if (prefix) {
    // Add prefix if missing
    const prefixPattern = new RegExp(`^/${prefix}(/|$)`);
    if (!prefixPattern.test(currentPath)) {
      correctedPath = `/${prefix}${currentPath}`;
    }
  }
  
  return `${correctedPath}${search}${hash}`;
};

export const getAssetUrl = (assetPath) => {
  const prefix = getCachedPrefix();
  if (!prefix) return assetPath;
  
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  return `/${prefix}${normalizedPath}`;
};
